import React, {useEffect, useRef, useState} from 'react';
import {Button, Form, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import {ModalType} from "../../../common/common-models";
import { Partner, PartnerCredential, PartnerCredentialStatus, PartnerCustomer, PartnerForm } from '../models';
import { EditPartnerConfirmModal } from './EditPartnerConfirmModal';
import { CreatePartnerCredentialModal } from '../../PartnerCredential/modals/CreatePartnerCredentialModal';
import { RevokePartnerCredentialConfirmModal } from '../../PartnerCredential/modals/RevokePartnerCredentialConfirmModal';
import { UpdatePartnerCredentialStatusConfirmModal } from '../../PartnerCredential/modals/UpdatePartnerCredentialStatusConfirmModal';
import { PartnerCredentialsTable } from '../../PartnerCredential/components/PartnerCredentialsTable';
import { PartnersService } from '../Partners.service';
import { isEmailValidAndPresent } from 'helpers/validator';
import CustomerService from 'services/customers.service';
import { PERSONAL_CODE_LENGTH, REGISTRY_CODE_LENGTH_EE, REGISTRY_CODE_LENGTH_LT } from 'common/constants';
import { isNullOrUndefined } from 'common/data-util';
import { onNumberChange } from 'helpers';

interface EditPartnerModalProps {
    partner?: Partner;
    modalType: ModalType;
    handleModalHide: () => void;
    loadPartners: () => void;
}

const DEFAULT_CARD_NUMBER_MASK_LENGTH = 6;
const DEFAULT_HOURLY_REQUEST_LIMIT = 100;
const DEFAULT_DAILY_REQUEST_LIMIT = 5000;
const MAX_ACTIVE_CREDENTIALS = 2;

export function EditPartnerModal(props: EditPartnerModalProps) {
    const [saving, setSaving]: [boolean, any] = useState(false);
    const [savingError, setSavingError]: [boolean, any] = useState(false);
    const [validated, setValidated]: [boolean, any] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible]: [boolean, any] = useState(false);
    const [form, setForm]: [PartnerForm, any] = useState({
        name: props.partner?.name,
        contactEmail: props.partner?.contactEmail,
        cardNumberMaskLength: props.partner?.cardNumberMaskLength ?? DEFAULT_CARD_NUMBER_MASK_LENGTH,
        isActive: props.partner?.isActive ?? true,
        hourlyRequestLimit: props.partner?.hourlyRequestLimit ?? DEFAULT_HOURLY_REQUEST_LIMIT,
        dailyRequestLimit: props.partner?.dailyRequestLimit ?? DEFAULT_DAILY_REQUEST_LIMIT,
        canReadDocuments: props.partner?.canReadDocuments ?? true,
        canReadCreditInfo: props.partner?.canReadCreditInfo ?? true,
        canReportCardPickup: props.partner?.canReportCardPickup ?? false,
        customerIds: props.partner?.customers?.map(c => c.id) ?? [],
    });
    const [customers, setCustomers] = useState<PartnerCustomer[]>(props.partner?.customers ?? []);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerSearching, setCustomerSearching] = useState(false);
    const [customerSearchError, setCustomerSearchError] = useState<string | null>(null);
    const [credentials, setCredentials] = useState<PartnerCredential[]>(props.partner?.credentials ?? []);
    const [credentialToRevoke, setCredentialToRevoke] = useState<PartnerCredential | null>(null);
    const [credentialToToggle, setCredentialToToggle] = useState<{credential: PartnerCredential, nextStatus: PartnerCredentialStatus} | null>(null);
    const [isCreateCredentialModalVisible, setIsCreateCredentialModalVisible] = useState(false);
    
    const customersScrollRef = useRef<HTMLDivElement>(null);
    const previousCustomerCountRef = useRef(customers.length);

    useEffect(() => {
        if (customers.length > previousCustomerCountRef.current && customersScrollRef.current) {
            customersScrollRef.current.scrollTop = customersScrollRef.current.scrollHeight;
        }

        previousCustomerCountRef.current = customers.length;
    }, [customers.length]);

    const isValidRegistryQuery = (value: string) => {
        const len = value.trim().length;
        return len === PERSONAL_CODE_LENGTH || len === REGISTRY_CODE_LENGTH_EE || len === REGISTRY_CODE_LENGTH_LT;
    };

    const handleCustomerSearch = async () => {
        const query = customerSearch.trim();
        if (!isValidRegistryQuery(query)) return;

        if (customers.some(c => c.registryNumber === query)) {
            setCustomerSearchError('Klient on juba lisatud');
            return;
        }

        setCustomerSearching(true);
        setCustomerSearchError(null);

        try {
            const result = await CustomerService.findByRegistryNumber(query);

            const customer: PartnerCustomer = {
                id: result.id,
                noomId: result.noomId,
                name: result.name,
                registryNumber: result.registryNumber,
            };

            setCustomers([...customers, customer]);
            setForm({
                ...form,
                customerIds: [...form.customerIds, result.id],
            });
            setCustomerSearch('');
        } catch (e : any) {
            console.error('Customer search failed', e);
            if (e.response && e.response.status === 404) {
                setCustomerSearchError('Klienti ei leitud');
            } else {
                setCustomerSearchError('Otsing ebaõnnestus');
            }
        } finally {
            setCustomerSearching(false);
        }
    };

    const handleRevokeClick = (credentialId: number) => {
        const credential = credentials.find(c => c.id === credentialId);
        if (credential) setCredentialToRevoke(credential);
    };

    const handleToggleActive = (credentialId: number, isActive: boolean) => {
        const credential = credentials.find(c => c.id === credentialId);
        if (!credential) return;
        
        const nextStatus = isActive ? PartnerCredentialStatus.ACTIVE : PartnerCredentialStatus.INACTIVE;
        
        setCredentialToToggle({credential, nextStatus});
    };

    const renderCreateButton = (disabled: boolean) => (
        <Button
            variant="primary"
            type="button"
            disabled={disabled}
            onClick={() => setIsCreateCredentialModalVisible(true)}
            className="d-flex align-items-center"
        >
            <i className="icon--16 icon__add d-flex mr-1"/>
            <span className='px-2'>Loo ligipääs</span>
        </Button>
    );

    const handleRemoveCustomer = (customerId: number) => {
        setCustomers(customers.filter(c => c.id !== customerId));
        setForm({
            ...form,
            customerIds: form.customerIds.filter(id => id !== customerId),
        });
    };

    const validateForm = () => {
        if (!form.name?.trim()) return false;
        if (form.contactEmail && !isEmailValidAndPresent(form.contactEmail)) return false;
        if (isNullOrUndefined(form.cardNumberMaskLength)) return false;
        if (isNullOrUndefined(form.hourlyRequestLimit)) return false;
        if (isNullOrUndefined(form.dailyRequestLimit)) return false;
        if (!form.customerIds || form.customerIds.length === 0) return false;
        return true;
    };

    const validateSubmit = () => {
        setValidated(false);
        const isValidForm = validateForm();
        
        setValidated(true);
        if (isValidForm) {
            setIsConfirmModalVisible(true);
        } else {
            setSaving(false);
        }
    };

    const handleSubmit = async () => {
        setSaving(true);
        setSavingError(false);

        try {
            if (props.modalType === ModalType.EDIT && props.partner) {
                await PartnersService.putPartner(props.partner.id, form);
            } else {
                await PartnersService.postPartner(form);
            }

            setTimeout(() => {
                props.handleModalHide();
                props.loadPartners();
            }, 300);
        } catch(e){
            console.error('Saving failed', e);
            setSavingError(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            show={true}
            size={"lg"}
            onHide={props.handleModalHide}
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    {
                        props.modalType === ModalType.CREATE &&
                        <span>Partneri lisamine</span>
                    }
                    {
                        props.modalType === ModalType.EDIT &&
                        <span>Partneri muutmine</span>
                    }
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form
                    noValidate
                    validated={validated} onSubmit={(e: any) => e.preventDefault()}
                    id={"partner-form"}
                    className={'mt-1 align-self-center m-0'}
                >
                    <Form.Group controlId={'form-partner-name'} className="row m-0">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Nimi*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                required
                                minLength={1}
                                value={form.name ?? ''}
                                onChange={(e) => setForm({
                                    ...form,
                                    name: e.target.value
                                })}
                                disabled={saving}
                                type="text"
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-partner-contact-email'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Kontakti e-mail</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                value={form.contactEmail ?? ''}
                                onChange={(e) => setForm({
                                    ...form,
                                    contactEmail: e.target.value
                                })}
                                disabled={saving}
                                type="email"
                                isInvalid={validated && !!form.contactEmail && !isEmailValidAndPresent(form.contactEmail)}
                            />
                            <Form.Control.Feedback type="invalid">Vigane e-mail</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-partner-card-number-mask-length'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Kaardinumbri maski pikkus*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                required
                                value={form.cardNumberMaskLength ?? ''}
                                onChange={(e) => setForm({
                                    ...form,
                                    cardNumberMaskLength: onNumberChange(e)
                                })}
                                disabled={saving}
                                type="number"
                                min={0}
                                onWheel={(e: any) => e.target.blur()}
                                isInvalid={validated && isNullOrUndefined(form.cardNumberMaskLength)}
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-partner-hourly-request-limit'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Päringute limiit tunnis*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                required
                                value={form.hourlyRequestLimit ?? ''}
                                onChange={(e) => setForm({
                                    ...form,
                                    hourlyRequestLimit: onNumberChange(e)
                                })}
                                disabled={saving}
                                type="number"
                                min={0}
                                onWheel={(e: any) => e.target.blur()}
                                isInvalid={validated && isNullOrUndefined(form.hourlyRequestLimit)}
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-partner-daily-request-limit'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Päringute limiit päevas*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                required
                                value={form.dailyRequestLimit ?? ''}
                                onChange={(e) => setForm({
                                    ...form,
                                    dailyRequestLimit: onNumberChange(e)
                                })}
                                disabled={saving}
                                type="number"
                                min={0}
                                onWheel={(e: any) => e.target.blur()}
                                isInvalid={validated && isNullOrUndefined(form.dailyRequestLimit)}
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    {
                        props.modalType === ModalType.EDIT && (
                            <Form.Group controlId={'form-partner-daily-request-limit'} className="row m-0 mt-3">
                                <Form.Label className={'col-5'}>
                                    <span className="text-wrap">Client id</span>
                                </Form.Label>
                                <div className="col p-0">
                                    <Form.Control
                                        required
                                        value={props.partner!.clientId}
                                        disabled
                                        min={0}
                                        onWheel={(e: any) => e.target.blur()}
                                        isInvalid={validated && isNullOrUndefined(form.dailyRequestLimit)}
                                    />
                                    <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                </div>
                            </Form.Group>
                        )
                    }

                    <Form.Group controlId={'form-partner-is-active'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Aktiivne</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-partner-is-active'
                                checked={form.isActive}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    isActive: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <h2 className='col mt-4'>Õigused*</h2>

                    <Form.Group controlId={'form-partner-can-read-documents'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Dokumentide lugemine</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-partner-can-read-documents'
                                checked={form.canReadDocuments}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    canReadDocuments: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-partner-can-read-credit-info'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Krediidiinfo lugemine</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-partner-can-read-credit-info'
                                checked={form.canReadCreditInfo}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    canReadCreditInfo: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-partner-can-report-card-pickup'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Kaardi kättesaamise teavitus (NOOM POS)</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-partner-can-report-card-pickup'
                                checked={form.canReportCardPickup}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    canReportCardPickup: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <h2 className='col mt-4'>Kliendid*</h2>

                    <Form.Group controlId={'form-partner-customer-search'} className="row m-0 mt-1">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Isiku- või registrikood</span>
                        </Form.Label>
                        <div className="col p-0">
                            <div className="input-group">
                                <Form.Control
                                    value={customerSearch}
                                    onChange={(e) => {
                                        setCustomerSearch(e.target.value);
                                        setCustomerSearchError(null);
                                    }}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (isValidRegistryQuery(customerSearch)) {
                                                handleCustomerSearch();
                                            }
                                        }
                                    }}
                                    disabled={saving || customerSearching}
                                    type="text"
                                />
                                <div className="input-group-append">
                                    <button
                                        className="btn btn-primary rounded d-flex align-items-center"
                                        type="button"
                                        disabled={saving || customerSearching || !isValidRegistryQuery(customerSearch)}
                                        onClick={handleCustomerSearch}
                                    >
                                        {customerSearching
                                            ? <Spinner size="sm" animation="border" />
                                            : <i className="icon icon__search d-inline-block"></i>}
                                    </button>
                                </div>
                            </div>
                            {customerSearchError && <div className="text-danger text-right mt-1">{customerSearchError}</div>}
                        </div>
                    </Form.Group>
                    {validated && customers.length === 0 && (
                        <div className="text-danger mt-1 ml-3 small text-right">Vähemalt üks klient on kohustuslik</div>
                    )}

                    {customers.length > 0 && (
                        <div ref={customersScrollRef} className="row m-0 mt-3" style={{maxHeight: 240, overflowY: 'auto'}}>
                            <table className='generic-table table table-responsive-md table-hover'>
                                <thead style={{position: 'sticky', top: 0, background: 'white', zIndex: 1, boxShadow: 'inset 0 -1px 0 #dee2e6'}}>
                                <tr>
                                    <th>ID</th>
                                    <th>Nimi</th>
                                    <th>Registrikood</th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>
                                {customers.map((c) => (
                                    <tr key={'customer-' + c.id}>
                                        <td className="text-nowrap">{c.noomId}</td>
                                        <td className="text-nowrap">{c.name}</td>
                                        <td className="text-nowrap">{c.registryNumber}</td>
                                        <td className="text-right">
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                className="d-flex align-items-center border-color-background border-radius-circle btn-outline-icon ml-auto"
                                                disabled={saving}
                                                onClick={() => handleRemoveCustomer(c.id)}
                                            >
                                                <i className="icon--16 my-2 icon__delete--outline-mask"/>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {
                        props.modalType === ModalType.EDIT && (() => {
                            const activeCredentialsCount = credentials.filter(c => c.status === PartnerCredentialStatus.ACTIVE).length;
                            const maxActiveReached = activeCredentialsCount >= MAX_ACTIVE_CREDENTIALS;
                            
                            return (
                                <>
                                    <div className="d-flex align-items-center justify-content-between mt-4 col p-0">
                                        <h2 className='col'>Ligipääsud</h2>
                                        { maxActiveReached ? (
                                            <OverlayTrigger 
                                                delay={250}
                                                overlay={
                                                    <Tooltip id="tooltip-create-credential">
                                                        <p style={{margin: '3px'}}>Maksimaalne aktiivsete ligipääsude arv ({MAX_ACTIVE_CREDENTIALS}) on saavutatud</p>
                                                    </Tooltip>
                                                } 
                                                placement={"left"}
                                            >
                                                <span className="d-inline-block">
                                                    {renderCreateButton(true)}
                                                </span>
                                            </OverlayTrigger>
                                        ) : ( renderCreateButton(saving) )}
                                    </div>

                                    { credentials.length === 0 && <div className="text-gray-700 mt-2 ml-3 small">Ligipääsud puuduvad...</div> }

                                    { credentials.length > 0 && (
                                        <PartnerCredentialsTable
                                            credentials={credentials}
                                            revokingCredentialId={credentialToRevoke?.id ?? null}
                                            togglingCredentialId={credentialToToggle?.credential.id ?? null}
                                            disabled={saving}
                                            onRevoke={handleRevokeClick}
                                            onToggleActive={handleToggleActive}
                                        />
                                    )}
                                </>
                            );
                        })()
                    }
                </Form>
            </Modal.Body>

            <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                <Button variant="info"
                        onClick={() => props.handleModalHide()}
                        type="button"
                        className={"d-flex align-items-center"}>
                    <span className='px-2'>Tühista</span>
                </Button>
                <Button variant="primary" onClick={validateSubmit} type="button" disabled={saving}
                        className="d-flex align-items-center">
                    {
                        !saving &&
                        <span className='px-2'>Salvesta</span>
                    }
                    {
                        saving &&
                        <>
                            <span className='px-2'>Salvestab…</span>
                            <Spinner size={"sm"} animation={"border"}/>
                        </>
                    }
                </Button>
            </Modal.Footer>
            {
                !saving && savingError &&
                <span className="d-flex justify-content-end text-danger px-2 pb-2">
                    Viga salvestamisel, proovi mõne hetke pärast uuesti.
                </span>
            }
            {
                validated && isConfirmModalVisible &&
                    <EditPartnerConfirmModal
                        handleConfirmSubmit={() => {
                            setIsConfirmModalVisible(false);
                            handleSubmit()
                        }}
                        handleConfirmReject={() => {
                            setSaving(false);
                            setIsConfirmModalVisible(false);
                        }}
                        modalType={props.modalType}/>
            }
            {
                isCreateCredentialModalVisible && props.partner && (
                    <CreatePartnerCredentialModal
                        partnerId={props.partner.id}
                        clientId={props.partner.clientId}
                        handleRejectClose={() => setIsCreateCredentialModalVisible(false)}
                        handleSubmitClose={(created) => {
                            setCredentials([...credentials, created]);
                            setIsCreateCredentialModalVisible(false);
                        }}
                    />
                )
            }
            {
                credentialToToggle && (
                    <UpdatePartnerCredentialStatusConfirmModal
                        credential={credentialToToggle.credential}
                        nextStatus={credentialToToggle.nextStatus}
                        handleRejectClose={() => setCredentialToToggle(null)}
                        handleSubmitClose={() => {
                            setCredentials(
                                credentials.map(c => c.id === credentialToToggle.credential.id
                                    ? {...c, status: credentialToToggle.nextStatus} : c
                                )
                            );
                            setCredentialToToggle(null);
                        }}
                    />
                )
            }
            {
                credentialToRevoke && (
                    <RevokePartnerCredentialConfirmModal
                        credential={credentialToRevoke}
                        handleRejectClose={() => setCredentialToRevoke(null)}
                        handleSubmitClose={() => {
                            setCredentials(
                                credentials.map(c => c.id === credentialToRevoke.id
                                    ? {...c, status: PartnerCredentialStatus.REVOKED} : c
                                )
                            );
                            setCredentialToRevoke(null);
                        }}
                    />
                )
            }
        </Modal>
    );
}
